import type {
  TypographyColor,
  TypographyElement,
  TypographyFont,
  TypographyType,
} from '@design/design-tokens/typography/TypographyTypes';
import {
  TYPOGRAPHY_COLORS,
  TYPOGRAPHY_ELEMENTS,
  TYPOGRAPHY_FONTS,
  TYPOGRAPHY_TYPES,
} from '@design/design-tokens/typography/TypographyTypes';
import clsx from 'clsx';
import { createElement, type HTMLAttributes, type ReactNode } from 'react';
import { z } from 'zod';

import styles from './Typography.module.scss';

export type {
  TypographyColor,
  TypographyElement,
  TypographyFont,
  TypographyType,
};

export const TYPOGRAPHY_MAX_VALUE_LENGTH = 2_000;

export const typographyPropsSchema = z
  .object({
    value: z.string().min(1).max(TYPOGRAPHY_MAX_VALUE_LENGTH),
    element: z.enum(TYPOGRAPHY_ELEMENTS).optional(),
    type: z.enum(TYPOGRAPHY_TYPES).optional(),
    font: z.enum(TYPOGRAPHY_FONTS).optional(),
    color: z.enum(TYPOGRAPHY_COLORS).optional(),
  })
  .strict();

export type TypographyDataProps = z.infer<typeof typographyPropsSchema>;

export interface TypographyProps
  extends Omit<HTMLAttributes<HTMLElement>, 'children'>,
    Omit<TypographyDataProps, 'value'> {
  element?: TypographyElement;
  type?: TypographyType;
  font?: TypographyFont;
  color?: TypographyColor;
  value: ReactNode;
}

const colorClassMap: Record<TypographyColor, string> = {
  default: styles.colorDefault,
  soft: styles.colorSoft,
  disabled: styles.colorDisabled,
  inverse: styles.colorInverse,
  primary: styles.colorPrimary,
  success: styles.colorSuccess,
  warning: styles.colorWarning,
  critical: styles.colorCritical,
  link: styles.colorLink,
};

/**
 * Primitive text component that applies typography scale, font family,
 * and text color tokens from the design system.
 */
export const Typography = ({
  element = 'p',
  type = 'bodyBase',
  font = 'nort',
  color = 'default',
  value,
  className,
  ...rest
}: TypographyProps) =>
  createElement(
    element,
    {
      'data-testid': 'typography',
      className: clsx(
        styles.root,
        styles[type],
        colorClassMap[color],
        font === 'robotoMono' && styles.fontRobotoMono,
        className
      ),
      ...rest,
    },
    value
  );
